
import { MealComposition, MealCompositionWithRecipes, DbMealComposition } from "@/types/mealComposition";
import { Recipe } from "@/types/recipe";
import { supabase } from "@/integrations/supabase/client";
import { authService } from "@/services/auth";
import { withErrorHandling } from "../utils/errorHandling";
import { mapDbMealCompositionToMealComposition, populateCompositionWithRecipes } from "./mealCompositionUtils";
import { recipeService } from "@/services/recipe";

/**
 * Service for querying meal compositions
 */
export const mealCompositionQueryService = {
  /**
   * Get all meal compositions for the current user and their family
   */
  getMealCompositions: withErrorHandling(
    async (): Promise<MealCompositionWithRecipes[]> => {
      const currentUser = await authService.getCurrentUser();
      
      if (!currentUser) {
        throw new Error("Failed to get current user");
      }
      
      // Fetch compositions - RLS will automatically filter to family compositions
      const result = await supabase
        .from("meal_compositions")
        .select("*")
        .order("created_at", { ascending: false });

      if (result.error) {
        throw result.error;
      }

      // Convert from database format
      const compositions = result.data.map((dbComposition) => 
        mapDbMealCompositionToMealComposition(dbComposition as DbMealComposition)
      );

      // Fetch all recipes to populate compositions
      const recipes = await recipeService.getRecipes();

      // Populate each composition with its recipes
      return compositions.map(composition => 
        populateCompositionWithRecipes(composition, recipes)
      );
    },
    "Error in getMealCompositions"
  ),

  /**
   * Get a meal composition by ID with populated recipe details
   */
  getMealCompositionById: withErrorHandling(
    async (id: string): Promise<MealCompositionWithRecipes | null> => {
      const currentUser = await authService.getCurrentUser();
      
      if (!currentUser) {
        throw new Error("Failed to get current user");
      }
      
      // Fetch the composition - RLS will ensure only accessible compositions are returned
      const result = await supabase
        .from("meal_compositions")
        .select("*")
        .eq("id", id)
        .single();

      if (result.error) {
        if (result.error.code === 'PGRST116') {
          // No rows returned - either doesn't exist or no access
          return null;
        }
        throw result.error;
      }

      if (!result.data) {
        return null;
      }

      // Convert from database format
      const composition = mapDbMealCompositionToMealComposition(result.data as DbMealComposition);

      // Fetch only the specific recipes for this composition
      const recipes = await recipeService.getRecipesByIds(composition.recipeIds);

      // Populate with recipe details
      return populateCompositionWithRecipes(composition, recipes);
    },
    "Error in getMealCompositionById"
  ),

  /**
   * Get multiple meal compositions by their IDs
   */
  getMealCompositionsByIds: withErrorHandling(
    async (ids: string[]): Promise<MealCompositionWithRecipes[]> => {
      if (ids.length === 0) {
        return [];
      }

      const currentUser = await authService.getCurrentUser();
      
      if (!currentUser) {
        throw new Error("Failed to get current user");
      }
      
      // Fetch compositions by IDs - RLS will filter accessible ones
      const result = await supabase
        .from("meal_compositions")
        .select("*")
        .in("id", ids);

      if (result.error) {
        throw result.error;
      }

      // Convert from database format
      const compositions = result.data.map((dbComposition) => 
        mapDbMealCompositionToMealComposition(dbComposition as DbMealComposition)
      );

      // Fetch all recipes to populate compositions
      const recipes = await recipeService.getRecipes();

      // Populate each composition with its recipes
      return compositions.map(composition => 
        populateCompositionWithRecipes(composition, recipes)
      );
    },
    "Error in getMealCompositionsByIds"
  )
};
